import { useEffect, useState } from "react";
import X from "lucide-react/dist/esm/icons/x";
import type { RankingResponse } from "../../game";
import { deviceKeyHash } from "../identity";

interface RankingSheetProps {
  open: boolean;
  onClose: () => void;
}

export function RankingSheet({ open, onClose }: RankingSheetProps) {
  const [ranking, setRanking] = useState<RankingResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(function manageRankingSheet() {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", closeOnEscape);
    return function restorePage() {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open, onClose]);

  useEffect(function loadRanking() {
    if (!open || ranking !== null) return;
    let cancelled = false;

    void (async () => {
      try {
        const me = await deviceKeyHash();
        const response = await fetch(`/api/ranking${me ? `?me=${me}` : ""}`);
        if (!response.ok) throw new Error(`ranking ${response.status}`);
        const data = (await response.json()) as RankingResponse;
        if (!cancelled) setRanking(data);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return function cancelRankingLoad() {
      cancelled = true;
    };
  }, [open, ranking]);

  if (!open) return null;

  return (
    <div className="sheet-backdrop" role="presentation" onPointerDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="rules-sheet" role="dialog" aria-modal="true" aria-labelledby="ranking-title">
        <header>
          <div>
            <span className="eyebrow">CHIN · ELO</span>
            <h2 id="ranking-title">Ranking</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Cerrar ranking" autoFocus>
            <X aria-hidden="true" size={22} />
          </button>
        </header>

        <div className="ranking-panel">
          {failed ? (
            <p className="ranking-empty">No pudimos cargar el ranking. Probá más tarde.</p>
          ) : ranking === null ? (
            <p className="ranking-empty">Cargando ranking…</p>
          ) : ranking.top.length === 0 ? (
            <p className="ranking-empty">
              Todavía no hay jugadores rankeados. Se entra con {ranking.minMatches} partidas jugadas.
            </p>
          ) : (
            <>
              <ol className="ranking-list">
                {ranking.top.map((player) => (
                  <li
                    key={`${player.position}-${player.name}`}
                    className={ranking.me?.position === player.position ? "is-me" : ""}
                  >
                    <span className="ranking-pos">{player.position}</span>
                    <span className="ranking-name">{player.name}</span>
                    <span className="ranking-record">{player.wins}–{player.losses}</span>
                    <b className="ranking-rating">{player.rating}</b>
                  </li>
                ))}
              </ol>
              {ranking.me && ranking.me.position > ranking.top.length ? (
                <div className="ranking-me">
                  <span className="ranking-pos">{ranking.me.position}</span>
                  <span className="ranking-name">{ranking.me.name} (vos)</span>
                  <span className="ranking-record">{ranking.me.wins}–{ranking.me.losses}</span>
                  <b className="ranking-rating">{ranking.me.rating}</b>
                </div>
              ) : null}
              {ranking.me && ranking.me.position === 0 ? (
                <p className="ranking-empty">
                  Te faltan {Math.max(0, ranking.minMatches - ranking.me.matches)} partidas para entrar al ranking.
                </p>
              ) : null}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
